import { AxiosError, isAxiosError } from "axios";
import { Booking } from "../models/BookingModel";
import { BookedTimes, ResourceModel, Resource } from "../models/ResourceModel";
import { del, get, post, put } from "./apiUtils";
import { LocationModel } from "../models/LocationModel";
import { ResourceType } from "../models/ResourceType";
import { apiRequest } from "./utils/APIRequest";
import { ApiResult } from "./utils/APIResult";
import { GenericResponse } from "../models/GenericModels";

export const getAvailableTimesForResource = async (resourceID: string) => {
  try {
    const response = await get<BookedTimes[]>(
      `/api/resources/available-times/${resourceID}`
    );

    response.forEach((bookedTime) => {
      bookedTime.startTime = new Date(bookedTime.startTime);
      bookedTime.endTime = new Date(bookedTime.endTime);
    });

    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    const groupedByMonth = response.reduce((acc, bookedTime) => {
      const month = monthNames[bookedTime.startTime.getMonth()];
      const year = bookedTime.startTime.getFullYear();
      const monthYear = `${month} ${year}`;
      if (!acc[monthYear]) {
        acc[monthYear] = [];
      }
      acc[monthYear].push(bookedTime);
      return acc;
    }, {} as { [key: string]: BookedTimes[] });

    const sortedGroupedByMonth = new Map(
      Object.keys(groupedByMonth)
        .sort((a, b) => {
          const [aMonth, aYear] = a.split(" ");
          const [bMonth, bYear] = b.split(" ");
          const aMonthIndex = monthNames.indexOf(aMonth);
          const bMonthIndex = monthNames.indexOf(bMonth);

          // Compare by year first, then by month
          const yearDiff = parseInt(aYear, 10) - parseInt(bYear, 10);
          if (yearDiff !== 0) return yearDiff;

          return aMonthIndex - bMonthIndex;
        })
        .map((key) => [key, groupedByMonth[key]]) // Convert to key-value pairs
    );

    // Convert Map back to an object if needed (e.g., for output or further processing)
    const sortedGroupedByMonthObject = Object.fromEntries(sortedGroupedByMonth);

    // console.log(sortedGroupedByMonthObject);

    return groupedByMonth;
  } catch (error) {
    console.error("Failed to get recent activity", error);
    return null;
  }
};

export const getResources = async () => {
  try {
    const response = await get<Resource[]>("/api/resources/all");
    return response;
  } catch (error) {
    const axiosError = error as AxiosError;
    console.error(`Failed to get resources`, error);
    throw axiosError;
  }
};

export const createBooking = async (bookingModel: Partial<Booking>) => {
  try {
    const { resourceId, userId, startTime, endTime, purpose } = bookingModel;

    const response = await post<GenericResponse>("/api/bookings/booking", {
      resourceId,
      userId,
      startTime,
      endTime,
      purpose,
    });

    return response;
  } catch (error: any) {
    const axiosError = error as AxiosError;
    console.error("Failed to update compliance policy", axiosError);
    return { success: false, message: axiosError.message };
  }
};

export const updateBooking = async (bookingModel: Partial<Booking>) => {
  try {
    const { id, resourceId, userId, startTime, endTime, purpose } =
      bookingModel;

    const response = await put<GenericResponse>(`/api/bookings/booking/${id}`, {
      resourceId,
      userId,
      startTime,
      endTime,
      purpose,
    });

    return response;
  } catch (error: any) {
    const axiosError = error as AxiosError;
    console.error("Failed to update compliance policy", axiosError);
    return { success: false, message: axiosError.message };
  }
};

export const getAllBookings = async () => {
  const colors = ["#3F4EB5", "#F9D3D2", "#8AC7AD", "#FFD370"];
  try {
    const response = await get<Booking[]>("/api/bookings/all");
    let colorIndex = 0;
    response.forEach((booking) => {
      booking.colour = colors[colorIndex];
      colorIndex = (colorIndex + 1) % colors.length; // Move sequentially and loop back
    });
    return response;
  } catch (error) {
    const axiosError = error as AxiosError;
    console.error("Failed to get all bookings", error);
    throw axiosError;
  }
};

export const getBookingsByUserID = async (userID: string) => {
  const colors = ["#3F4EB5", "#F9D3D2", "#8AC7AD", "#FFD370"];
  try {
    const response = await get<Booking[]>(`/api/bookings/user/${userID}`);
    let colorIndex = 0;
    response.forEach((booking) => {
      booking.colour = colors[colorIndex];
      colorIndex = (colorIndex + 1) % colors.length; // Move sequentially and loop back
    });
    return response;
  } catch (error) {
    const axiosError = error as AxiosError;
    console.error(`Failed to get bookings for user ${userID}`, error);
    throw axiosError;
  }
};

export const getAllLocations = async () => {
  try {
    const response = await get<LocationModel[]>("/api/resources/all-locations");
    return response;
  } catch (error) {
    const axiosError = error as AxiosError;
    console.error("Failed to delete booking", error);
    throw axiosError;
  }
};

export const getResourcesByLocation = async (locationID: string) => {
  try {
    const response = await get<Resource[]>(
      `/api/resources/by-location/${locationID}`
    );
    return response;
  } catch (error) {
    const axiosError = error as AxiosError;
    console.error(`Failed to get resources for location ${locationID}`, error);
    throw axiosError;
  }
};

export const deleteBooking = async (bookingId: string) => {
  try {
    const response = await del<Booking[]>(`/api/bookings/booking/${bookingId}`);
    return response;
  } catch (error) {
    const axiosError = error as AxiosError;
    console.error("Failed to get all locations", error);
    throw axiosError;
  }
};

export const deleteResource = async (resourceId: string) => {
  try {
    const response = await del<Resource[]>(
      `/api/resources/resource/${resourceId}`
    );
    return response;
  } catch (error) {
    const axiosError = error as AxiosError;
    console.error(`Failed to delete resource with ID ${resourceId}`, error);
    throw axiosError;
  }
};

export const getAllResourceTypes = async () => {
  try {
    const response = await get<ResourceType[]>("/api/resource-types/all");
    return response;
  } catch (error) {
    const axiosError = error as AxiosError;
    console.error("Failed to get all resource types", error);
    throw axiosError;
  }
};

export const updateResource = async (
  resourceId: string,
  updatedResource: Partial<Resource>
): Promise<ApiResult<Resource>> => {
  return await apiRequest(() =>
    put<Resource>(`api/resources/resource/${resourceId}`, updatedResource)
  );
};

export const createResource = async (
  resource: ResourceModel
): Promise<ApiResult<Resource>> => {
  return await apiRequest(() =>
    post<Resource>(`api/resources/resource`, resource)
  );
};

export const updateBookingStatus = async (
  bookingId: string,
  status: string
) => {
  try {
    const response = await put<GenericResponse>(
      `/api/bookings/update-booking-status/${bookingId}`,
      { status }
    );
    return response;
  } catch (error) {
    const axiosError = error as AxiosError;
    console.error("Failed to update compliance policy", axiosError);
    const message =
      axiosError.response && typeof axiosError.response.data === "object"
        ? (axiosError.response.data as { message?: string }).message ||
          "Unknown error"
        : "Unknown error";
    return { success: false, message };
  }
};

export const getBookingByID = async (bookingId: string) => {
  try {
    const response = await get<GenericResponse>(
      `/api/bookings/booking/${bookingId}`
    );
    return response;
  } catch (error) {
    const axiosError = error as AxiosError;
    console.error("Failed to update compliance policy", axiosError);
    const message =
      axiosError.response && typeof axiosError.response.data === "object"
        ? (axiosError.response.data as { message?: string }).message ||
          "Unknown error"
        : "Unknown error";
    return { success: false, message };
  }
};
